import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Progress,
  Skeleton,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';

import { PositionsEntityKind } from '@revelio/data-access';

import type { CompanyRicsIds } from '../hooks/useCompanyRics';
import { useCurrencyFormatter } from '../hooks/useCurrencyFormatter';
import { REGIONS, useGeoSalaryUplift } from '../hooks/useGeoSalaryUplift';
import { useGeoTopCompanies } from '../hooks/useGeoTopCompanies';
import type {
  ResolvedProfileIds,
  RoleLevel,
} from '../hooks/useResolveProfileIds';
import type { UpliftMode } from '../IndividualPotentialPage';
import { SectionCard } from '../SectionCard';
import { GeoUpliftTable } from './GeoUpliftTable';

const LARGE_COMPANY_THRESHOLD = 5000;

const getEmptyStateHint = (
  selectedRegionId: number | null,
  lookOutside: boolean,
  userCountryName: string | null
): string => {
  if (selectedRegionId === null) {
    return ` in ${userCountryName ?? 'your country'}. Try selecting a region to see countries.`;
  }
  if (!lookOutside) {
    return ' within your company. Try switching to "All Companies".';
  }
  return ' in this region.';
};

interface GeographicMobilitySectionProps {
  resolvedIds: ResolvedProfileIds;
  rics: CompanyRicsIds;
  isResolving: boolean;
  userSalary: number;
  upliftMode: UpliftMode;
  userPercentile: number | null;
  userRoleLevel: RoleLevel | null;
}

export const GeographicMobilitySection = ({
  resolvedIds,
  rics,
  isResolving,
  userSalary,
  upliftMode,
  userPercentile,
  userRoleLevel,
}: GeographicMobilitySectionProps) => {
  const isLargeCompany = (rics.employeeCount ?? 0) >= LARGE_COMPANY_THRESHOLD;

  const [lookOutside, setLookOutside] = useState(true);

  // null = "In Country" (MSAs/cities within user's country)
  // number = a region ID (countries within that region)
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null);

  const formatCurrency = useCurrencyFormatter();

  const { geoUplifts, userPppFactor, isLoading, percentileProgress, error } =
    useGeoSalaryUplift(
      resolvedIds,
      rics,
      userSalary,
      lookOutside,
      selectedRegionId,
      upliftMode,
      userPercentile
    );

  const { fetchCompanies, getCompanies } = useGeoTopCompanies(
    resolvedIds,
    userSalary
  );

  // Resolve user's geo levels for display, highlight, and region picker
  const { userCountryName, userRegionId, userGeoIds } = useMemo(() => {
    const countryGeo = resolvedIds.geoLevels.find((g) => g.type === 'country');
    const regionGeo = resolvedIds.geoLevels.find((g) => g.type === 'region');

    // Collect all of the user's geo IDs so the table can exact-match
    const ids = new Set<number>();
    for (const g of resolvedIds.geoLevels) {
      ids.add(g.id);
    }

    return {
      userCountryName: countryGeo?.name ?? null,
      userRegionId: regionGeo?.id ?? null,
      userGeoIds: ids,
    };
  }, [resolvedIds.geoLevels]);

  // Label for the geo scope dropdown
  const geoScopeLabel = useMemo(() => {
    if (selectedRegionId === null) {
      return userCountryName ?? 'In Country';
    }
    const region = REGIONS.find((r) => r.id === selectedRegionId);
    return region?.longName ?? 'Region';
  }, [selectedRegionId, userCountryName]);

  // Fetch top companies for a geo when user expands a row
  const handleExpandGeo = (geoId: number) => {
    if (lookOutside) {
      const geoEntry = geoUplifts.find((g) => g.geoId === geoId);
      const entityType =
        geoEntry?.geoType === 'msa'
          ? PositionsEntityKind.Msa
          : PositionsEntityKind.Country;
      fetchCompanies(geoId, entityType);
    }
  };

  if (!resolvedIds.roleId && !isResolving) {
    return (
      <SectionCard>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          Unable to resolve your role. Geographic mobility insights require a
          recognized role to compare salaries across locations.
        </Alert>
      </SectionCard>
    );
  }

  if (error && !isLoading) {
    return (
      <SectionCard>
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </SectionCard>
    );
  }

  return (
    <SectionCard spacing={4} testId="geo-mobility-section">
      {/* Header */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        <Box>
          <Text fontSize="lg" fontWeight="bold" color="gray.800">
            Where Could You Earn More?
          </Text>
          <Text fontSize="sm" color="gray.500">
            Salary uplift by geography for your role
            {resolvedIds.roleName ? ` (${resolvedIds.roleName})` : ''}
          </Text>
        </Box>

        <Flex gap={3} wrap="wrap" align="center">
          {/* Geo scope: dropdown with "In Country" + regions */}
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              rightIcon={<ChevronDownIcon />}
              variant="outline"
              colorScheme="teal"
              maxW="220px"
            >
              <Text noOfLines={1}>{geoScopeLabel}</Text>
            </MenuButton>
            <MenuList maxH="300px" overflowY="auto" zIndex={10}>
              <MenuItem
                onClick={() => setSelectedRegionId(null)}
                fontWeight={selectedRegionId === null ? 'bold' : 'normal'}
                bg={selectedRegionId === null ? 'teal.50' : undefined}
                fontSize="sm"
              >
                {userCountryName ?? 'In Country'} (cities)
              </MenuItem>
              {userRegionId !== null && (
                <MenuItem
                  onClick={() => setSelectedRegionId(userRegionId)}
                  fontWeight={
                    selectedRegionId === userRegionId ? 'bold' : 'normal'
                  }
                  bg={selectedRegionId === userRegionId ? 'teal.50' : undefined}
                  fontSize="sm"
                >
                  {REGIONS.find((r) => r.id === userRegionId)?.longName ??
                    'Your Region'}{' '}
                  (countries)
                </MenuItem>
              )}
              {REGIONS.filter((r) => r.id !== userRegionId).map((region) => (
                <MenuItem
                  key={region.id}
                  onClick={() => setSelectedRegionId(region.id)}
                  fontWeight={
                    selectedRegionId === region.id ? 'bold' : 'normal'
                  }
                  bg={selectedRegionId === region.id ? 'teal.50' : undefined}
                  fontSize="sm"
                >
                  {region.longName}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>

          {/* Company scope toggle — only show if large company */}
          {isLargeCompany && (
            <ButtonGroup size="sm" isAttached variant="outline">
              <Tooltip
                label="Compare salaries within your current company"
                fontSize="xs"
              >
                <Button
                  onClick={() => setLookOutside(false)}
                  colorScheme={!lookOutside ? 'blue' : 'gray'}
                  variant={!lookOutside ? 'solid' : 'outline'}
                >
                  My Company
                </Button>
              </Tooltip>
              <Tooltip
                label="Compare salaries across all companies"
                fontSize="xs"
              >
                <Button
                  onClick={() => setLookOutside(true)}
                  colorScheme={lookOutside ? 'blue' : 'gray'}
                  variant={lookOutside ? 'solid' : 'outline'}
                >
                  All Companies
                </Button>
              </Tooltip>
            </ButtonGroup>
          )}
        </Flex>
      </Flex>

      {/* Table */}
      {(isLoading || isResolving) && (
        <>
          <Skeleton h="300px" borderRadius="md" />
          {upliftMode === 'percentile' &&
            percentileProgress > 0 &&
            percentileProgress < 1 && (
              <Progress
                value={percentileProgress * 100}
                size="xs"
                colorScheme="blue"
                borderRadius="full"
              />
            )}
        </>
      )}

      {!isLoading && !isResolving && geoUplifts.length > 0 && (
        <GeoUpliftTable
          geoUplifts={geoUplifts}
          userGeoIds={userGeoIds}
          userPppFactor={userPppFactor}
          lookOutside={lookOutside}
          formatCurrency={formatCurrency}
          onExpandGeo={handleExpandGeo}
          getCompanies={getCompanies}
          upliftMode={upliftMode}
          userSalary={userSalary}
          userRoleLevel={userRoleLevel}
        />
      )}

      {!isLoading && !isResolving && geoUplifts.length === 0 && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          No geographic salary data available for this role
          {getEmptyStateHint(selectedRegionId, lookOutside, userCountryName)}
        </Alert>
      )}

      {/* Info footer */}
      <Text fontSize="2xs" color="gray.400">
        {upliftMode === 'percentile'
          ? 'Salaries shown at your pay percentile. '
          : 'Salaries shown as median base compensation. '}
        {selectedRegionId === null
          ? `Showing top cities/metros in ${userCountryName ?? 'your country'}. `
          : `Showing top countries in ${REGIONS.find((r) => r.id === selectedRegionId)?.longName ?? 'selected region'}. `}
        Cost of living adjusted using World Bank PPP data (2023).
      </Text>
    </SectionCard>
  );
};
